import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const r2 = (n) => Math.round((n || 0) * 100) / 100;

function ListingsBlock({ listings, hidden, toggle, hideAll, busy }) {
  if (!listings.length) return null;
  const hiddenCount = listings.filter((l) => hidden[l.id]).length;
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid="release-listings">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800">Live listings <span className="font-normal text-slate-500" data-testid="release-hidden-count">({hiddenCount} of {listings.length} hidden)</span></p>
        <button type="button" disabled={busy} data-testid="release-hide-all" onClick={hideAll} className="text-xs font-semibold text-primary hover:underline">Hide all</button>
      </div>
      {listings.map((l) => (
        <div key={l.id} className="flex items-center justify-between gap-2 py-1 text-sm" data-testid="release-listing-row">
          <span className={`truncate ${hidden[l.id] ? "text-slate-400 line-through" : "text-slate-700"}`}>{l.title}</span>
          {l.status === "locked"
            ? <span className="shrink-0 text-xs text-amber-700">Being purchased</span>
            : <Switch checked={!hidden[l.id]} disabled={busy} onCheckedChange={() => toggle(l)} data-testid={`release-hide-${l.id}`} aria-label={hidden[l.id] ? "Show listing" : "Hide listing"}
                className={hidden[l.id] ? "data-[state=unchecked]:bg-red-200" : "data-[state=checked]:bg-emerald-300"} />}
        </div>
      ))}
      <p className="mt-2 text-xs text-slate-500">Hidden listings stay in your profile — you can relist them after topping up your deposit.</p>
    </div>
  );
}

function OrdersBlock({ orders, auto, setAuto }) {
  if (!orders.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid="release-orders">
      <p className="mb-1 text-sm font-semibold text-slate-800">Orders in progress ({orders.length})</p>
      {orders.map((o) => <p key={o.id} className="text-xs text-slate-600" data-testid="release-order-row">{o.ref} · {o.title}</p>)}
      <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="release-auto-check" className="h-4 w-4" />
        Release my deposit automatically when these finish
      </label>
    </div>
  );
}

export function DepositReleaseModal({ open, onClose, onDone }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(false);
  const [shown, setShown] = useState([]);
  const [hidden, setHidden] = useState({});
  const load = () => api.get("/deposit/release-preview").then(({ data }) => {
    setSt(data);
    setShown((prev) => { const m = new Map(prev.map((l) => [l.id, l])); data.listings.forEach((l) => m.set(l.id, l)); return [...m.values()]; });
  }).catch(() => toast.error("Could not load your deposit details"));
  useEffect(() => { if (open) { setSt(null); setAuto(false); setShown([]); setHidden({}); load(); } }, [open]);

  const setOne = async (l, hide) => {
    await api.patch(`/listings/${l.id}/${hide ? "pause" : "reactivate"}`);
    setHidden((h) => ({ ...h, [l.id]: hide }));
  };
  const toggle = async (l) => { setBusy(true); try { await setOne(l, !hidden[l.id]); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not update listing"); } setBusy(false); };
  const hideAll = async () => { setBusy(true); for (const l of shown.filter((x) => x.status !== "locked" && !hidden[x.id])) { try { await setOne(l, true); } catch { /* shown below */ } } await load(); setBusy(false); };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/deposit/request-release", { auto_when_done: auto });
      if (data.ok) { toast.success(data.queued ? "We'll request your refund as soon as your orders finish." : `Your refund of AED ${r2(data.net)} is on its way. We'll email you when it's sent.`); onDone(); onClose(); }
      else toast.error(data.blockers?.[0] || "Could not request your refund");
    } catch { toast.error("Could not request your refund"); }
    setBusy(false);
  };

  const hasOrders = st && (st.orders.length > 0 || st.open_disputes > 0);
  const ready = st && !st.need_bank && st.listings.length === 0 && (!hasOrders || auto);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="deposit-release-modal">
        <DialogHeader><DialogTitle>Request your deposit refund</DialogTitle><DialogDescription>Check everything below, then request your refund.</DialogDescription></DialogHeader>
        {!st ? <p className="text-sm text-slate-500">Loading…</p> : (
          <div className="space-y-3">
            <div className="rounded-xl bg-blue-50 p-3 text-sm text-blue-900" data-testid="deposit-release-breakdown">
              {st.balance_applied > 0 ? `Deposit AED ${r2(st.deposit)} − balance owed AED ${r2(st.balance_applied)} = AED ${r2(st.net)}` : `Deposit AED ${r2(st.deposit)} = AED ${r2(st.net)} to your bank`}
            </div>
            <ListingsBlock listings={shown} hidden={hidden} toggle={toggle} hideAll={hideAll} busy={busy} />
            <OrdersBlock orders={st.orders} auto={auto} setAuto={setAuto} />
            {st.open_disputes > 0 && !st.orders.length && <p className="text-xs text-amber-700">You have {st.open_disputes} concern(s) under review.</p>}
            {st.need_bank && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="release-need-bank">Add your bank details to receive the refund — <Link to="/payments" className="font-semibold underline" data-testid="release-payments-link">Payments &amp; verification</Link></p>}
            {ready && <Button disabled={busy} data-testid="deposit-release-confirm-btn" onClick={submit} className="w-full rounded-full bg-primary font-semibold">{hasOrders ? "Request when my orders finish" : "Request my refund"}</Button>}
            {!ready && !st.need_bank && <p className="text-center text-xs text-slate-500" data-testid="release-not-ready">{st.listings.length ? "Hide your live listings to continue." : "Tick the box above to have your deposit released when your orders finish."}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
